import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation, useMatch } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "../mock/mock";
import MayaLogo from "./MayaLogo";
import { useSidebar } from "../hooks/useSidebar";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";
import {
  LayoutDashboard,
  TrendingUp,
  BookOpen,
  ClipboardList,
  User,
  LogOut,
  ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Final navigation order:
 *   Dashboard → Progress → Study Resources (accordion) → Interview Tips → Maya → Profile
 *   Large gap, then Log Out at the very bottom.
 */

/* -------- shared row styles -------- */
const rowBase =
  "flex items-center rounded-xl text-[14px] font-medium transition";
const rowIdle = "text-slate-600 hover:bg-slate-50 hover:text-[#1d4ed8]";
const rowActive = "bg-[#1d4ed8] text-white shadow-sm";

/* -------- Study Resources accordion parent -------- */
const StudyResourcesAccordion = ({ collapsed, guides }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const resourceMatch = useMatch("/resources");
  const guideMatch = useMatch("/resources/:guideSlug/:topicSlug");
  const isOnResources = Boolean(resourceMatch) || Boolean(guideMatch);

  // Which guide is "active"? Prefer URL param (topic route) then query string.
  const activeGuideSlug = useMemo(() => {
    if (guideMatch?.params?.guideSlug) return guideMatch.params.guideSlug;
    const params = new URLSearchParams(location.search);
    return params.get("guide") || null;
  }, [guideMatch, location.search]);

  const [open, setOpen] = useState(isOnResources);
  useEffect(() => {
    // Keep accordion open while the user is inside Study Resources
    if (isOnResources) setOpen(true);
  }, [isOnResources]);

  const parentActive = isOnResources && !activeGuideSlug;

  const goToHub = (e) => {
    e.preventDefault();
    navigate("/resources");
  };

  // Collapsed sidebar → single icon that also navigates to the hub.
  if (collapsed) {
    return (
      <NavLink
        to="/resources"
        end
        data-testid="sidebar-link-study-resources"
        title="Study Resources"
        className={({ isActive }) =>
          `${rowBase} justify-center px-0 py-2.5 ${isActive || isOnResources ? rowActive : rowIdle}`
        }
      >
        <BookOpen className="w-[18px] h-[18px] shrink-0" />
      </NavLink>
    );
  }

  return (
    <div data-testid="sidebar-study-resources">
      <div
        className={`${rowBase} gap-3 px-4 py-2.5 cursor-pointer ${
          parentActive ? rowActive : rowIdle
        }`}
      >
        {/* Left region opens the Learning Hub */}
        <button
          type="button"
          onClick={goToHub}
          data-testid="sidebar-link-study-resources"
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <BookOpen className="w-[18px] h-[18px] shrink-0" />
          <span className="whitespace-nowrap">Study Resources</span>
        </button>
        {/* Right chevron toggles the accordion */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Collapse Study Resources" : "Expand Study Resources"}
          data-testid="sidebar-study-resources-toggle"
          className={`shrink-0 -mr-1 p-1 rounded-md transition ${
            parentActive ? "hover:bg-white/15" : "hover:bg-slate-100"
          }`}
        >
          <ChevronRight
            className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
          />
        </button>
      </div>

      {/* Accordion panel */}
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-1 ml-4 pl-3 border-l border-slate-100 space-y-0.5">
            {guides.length === 0 ? (
              <p className="text-[12px] text-slate-400 py-1.5 pl-2">Loading guides…</p>
            ) : (
              guides.map((g) => {
                const isActive = activeGuideSlug === g.slug;
                return (
                  <button
                    key={g.slug}
                    type="button"
                    onClick={() => navigate(`/resources?guide=${g.slug}`)}
                    data-testid={`sidebar-guide-${g.slug}`}
                    className={`w-full text-left flex items-center gap-2 pl-2.5 pr-2 py-2 rounded-lg text-[13px] font-medium transition ${
                      isActive
                        ? "bg-[#eaf1ff] text-[#1d4ed8]"
                        : "text-slate-500 hover:bg-slate-50 hover:text-[#1d4ed8]"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 transition ${
                        isActive ? "bg-[#1d4ed8]" : "bg-slate-300"
                      }`}
                    />
                    <span className="truncate">{g.title}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* -------- Top-level items rendered as NavLinks -------- */
const primaryItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/progress", label: "Progress", icon: TrendingUp },
];
const secondaryItems = [
  { to: "/dashboard/interviews", label: "Interview Tips", icon: ClipboardList },
  { to: "/maya", label: "__MAYA__" },
  { to: "/profile", label: "Profile", icon: User },
];

const DashboardSidebar = () => {
  const navigate = useNavigate();
  const { collapsed: rawCollapsed, mobileOpen, closeMobile } = useSidebar();
  // On mobile the drawer is always full-width — ignore the desktop-collapsed flag.
  const collapsed = rawCollapsed && !mobileOpen;
  const { logout } = useAuth();
  const [guides, setGuides] = useState([]);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      toast.success("You have been signed out.");
    } catch (_) { /* logout tolerates failure */ }
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/guides`)
      .then((r) => mounted && setGuides(r.data.guides || []))
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const renderLink = ({ to, label, icon: Icon }) => (
    <NavLink
      key={to}
      to={to}
      end={to === "/dashboard"}
      data-testid={`sidebar-link-${label === "__MAYA__" ? "maya" : label.toLowerCase().replace(/\s+/g, "-")}`}
      title={collapsed ? (label === "__MAYA__" ? "Maya" : label) : undefined}
      className={({ isActive }) =>
        `${rowBase} ${collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-4 py-2.5"} ${
          isActive ? rowActive : rowIdle
        }`
      }
    >
      {({ isActive }) =>
        label === "__MAYA__" ? (
          <>
            <MayaLogo
              variant="on-white"
              className="h-5 shrink-0"
              style={isActive ? { filter: "brightness(0) invert(1)" } : undefined}
            />
            {!collapsed && <span className="sr-only">Maya</span>}
          </>
        ) : (
          <>
            {Icon && <Icon className="w-[18px] h-[18px] shrink-0" />}
            {!collapsed && <span className="whitespace-nowrap">{label}</span>}
          </>
        )
      }
    </NavLink>
  );

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={closeMobile}
        aria-hidden
        className={`lg:hidden fixed inset-0 z-30 bg-slate-900/40 transition-opacity duration-300 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="dashboard-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        data-mobile-open={mobileOpen ? "true" : "false"}
        className={`
          fixed lg:sticky top-0 z-40 lg:z-10
          flex flex-col shrink-0 bg-white border-r border-slate-100
          h-screen lg:min-h-screen overflow-y-auto overflow-x-hidden
          transition-transform lg:transition-[width] duration-300 ease-in-out
          w-64 ${collapsed ? "lg:w-[76px]" : "lg:w-64"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
      <div className={`p-6 flex items-center ${collapsed ? "justify-center px-3" : ""}`}>
        <img
          src={LOGO_URL}
          alt="Medily"
          className={`cursor-pointer transition-all duration-300 ${collapsed ? "h-7" : "h-8"}`}
          style={collapsed ? { width: 30, objectFit: "contain", objectPosition: "left" } : undefined}
          onClick={() => navigate("/")}
        />
      </div>

      <nav className={`flex-1 space-y-1 ${collapsed ? "px-2" : "px-3"}`}>
        {primaryItems.map(renderLink)}
        <StudyResourcesAccordion collapsed={collapsed} guides={guides} />
        {secondaryItems.map(renderLink)}
      </nav>

      {/* Logout — sits closer to Profile per Stage 4 spec */}
      <div className={`${collapsed ? "px-2" : "px-3"} pt-3`}>
        <button
          data-testid="sidebar-logout"
          onClick={handleLogout}
          disabled={loggingOut}
          title={collapsed ? "Log Out" : undefined}
          className={`w-full ${rowBase} ${
            collapsed ? "justify-center px-0 py-2.5" : "gap-3 px-4 py-2.5"
          } text-slate-600 hover:bg-slate-50 hover:text-[#dc2626] disabled:opacity-70`}
        >
          <LogOut className="w-[18px] h-[18px] shrink-0" />
          {!collapsed && <span>Log Out</span>}
        </button>
      </div>

      {!collapsed && (
        <div className="px-3 pt-6 mt-auto">
          <div
            className="rounded-2xl bg-gradient-to-b from-[#eef4ff] to-white border border-slate-100 p-4 text-center"
            data-testid="sidebar-practice-card"
          >
            <MayaLogo variant="on-white" className="h-6 mx-auto" />
            <p className="mt-3 text-sm font-semibold text-slate-900">Need Practice?</p>
            <p className="text-xs text-slate-500 mt-1">
              Practise realistic MMI stations tailored to your chosen medical school.
            </p>
            <button
              onClick={() => navigate("/maya")}
              data-testid="sidebar-start-practising"
              className="mt-3 w-full py-2 rounded-full bg-[#1d4ed8] text-white text-sm font-medium hover:bg-[#1e40af] transition"
            >
              Start Practising
            </button>
          </div>
        </div>
      )}

      {collapsed && <div className="mt-auto" />}
      </aside>
    </>
  );
};

export default DashboardSidebar;
